import api from './api';

/* Where the HR app installers actually live.
 *
 * The download URLs are NOT hardcoded here. /list_hr_apps is authenticated (the
 * pages that use it are behind login, so the API client sends the token) and
 * returns the current blob for each app_type along with its storage URL. That
 * URL is world-readable, so it can go straight in an <a href> — no
 * Authorization header is involved in the download itself, which is what makes
 * plain browser navigation work.
 *
 * Resolving instead of hardcoding also means a renamed or re-versioned build
 * keeps working, and the size / updated date shown in the UI are the real ones
 * rather than numbers someone has to remember to edit.
 */

/** app_type values the API reports. */
export type HrAppType =
  | 'hr_payroll'
  | 'work_monitor'
  | 'messenger_windows'
  | 'messenger_mac';

export interface HrAppBuild {
  filename: string;
  blob_path: string;
  size: number;
  updated: string | null;
  public_url: string;
  /** null for a blob whose name matches no known app_type. */
  app_type: HrAppType | null;
}

interface ListHrAppsResponse {
  apps: string[];
  items: HrAppBuild[];
}

export const listHrApps = async (): Promise<HrAppBuild[]> => {
  const res = await api.get<ListHrAppsResponse>('/list_hr_apps');
  return res.data?.items ?? [];
};

/** { [app_type]: build } — blobs with no recognised app_type are dropped. */
export const getHrAppBuilds = async (): Promise<Partial<Record<HrAppType, HrAppBuild>>> => {
  const items = await listHrApps();
  const byType: Partial<Record<HrAppType, HrAppBuild>> = {};
  for (const item of items) {
    if (item.app_type) byType[item.app_type] = item;
  }
  return byType;
};

/** "258 MB" — the API reports bytes; the UI wants something readable. */
export const formatBuildSize = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '—';
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
};

/** "18 Aug 2026" from the API's ISO timestamp. */
export const formatBuildDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};
